'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Bell, 
  Settings, 
  HelpCircle, 
  Plus,
  Filter,
  Download,
  RefreshCw,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface TopBarProps {
  title: string;
  subtitle?: string;
  onRefresh?: () => void;
  onAdd?: () => void;
  showAddButton?: boolean;
}

export default function TopBar({ 
  title, 
  subtitle, 
  onRefresh, 
  onAdd,
  showAddButton = true 
}: TopBarProps) {
  const [searchValue, setSearchValue] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [notifications, setNotifications] = useState([
    { id: 1, title: '核心交换机离线', time: '2分钟前', type: 'error', read: false },
    { id: 2, title: '带宽使用率超过90%', time: '15分钟前', type: 'warning', read: false },
    { id: 3, title: '固件更新可用', time: '1小时前', type: 'info', read: true },
  ]);
  const [showNotifications, setShowNotifications] = useState(false);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  return (
    <header className="h-16 bg-white border-b border-[var(--border)] flex items-center justify-between px-6 sticky top-0 z-30">
      {/* 左侧标题 */}
      <div>
        <h1 className="text-xl font-semibold text-[var(--foreground)]">{title}</h1>
        {subtitle && (
          <p className="text-sm text-[var(--muted-foreground)]">{subtitle}</p>
        )}
      </div>

      {/* 右侧操作区 */}
      <div className="flex items-center gap-3">
        {/* 搜索框 */}
        <div className={cn(
          "relative flex items-center transition-all duration-200",
          isSearchFocused ? "w-80" : "w-64"
        )}>
          <Search className={cn(
            "absolute left-3 w-4 h-4 transition-colors",
            isSearchFocused ? "text-[var(--primary)]" : "text-[var(--muted-foreground)]"
          )} />
          <input
            type="text"
            placeholder="搜索设备、IP地址..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            className={cn(
              "w-full pl-10 pr-9 py-2 text-sm rounded-lg border transition-all duration-200 outline-none",
              isSearchFocused 
                ? "border-[var(--primary)] ring-2 ring-[var(--primary)]/10 bg-white" 
                : "border-[var(--border)] bg-[var(--secondary)] hover:bg-white"
            )}
          />
          {searchValue && (
            <button 
              onClick={() => setSearchValue('')}
              className="absolute right-3 text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* 筛选按钮 */}
        <button className="p-2 rounded-lg border border-[var(--border)] text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:border-[var(--foreground)]/20 transition-colors">
          <Filter className="w-4 h-4" />
        </button>

        {/* 刷新按钮 */}
        <button 
          onClick={onRefresh}
          className="p-2 rounded-lg border border-[var(--border)] text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:border-[var(--foreground)]/20 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
        </button>

        {/* 导出按钮 */}
        <button className="p-2 rounded-lg border border-[var(--border)] text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:border-[var(--foreground)]/20 transition-colors">
          <Download className="w-4 h-4" />
        </button>

        {/* 分隔线 */}
        <div className="w-px h-6 bg-[var(--border)]" />

        {/* 通知 */}
        <div className="relative">
          <button 
            onClick={() => setShowNotifications(!showNotifications)}
            className={cn(
              "relative p-2 rounded-lg transition-colors",
              showNotifications 
                ? "bg-[var(--accent)] text-[var(--primary)]" 
                : "text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:bg-[var(--secondary)]"
            )}
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[var(--destructive)] text-white text-xs font-medium rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>

          {/* 通知下拉 */}
          <AnimatePresence>
            {showNotifications && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-[var(--border)] overflow-hidden z-50"
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
                  <h3 className="font-medium text-[var(--foreground)]">通知</h3>
                  <button 
                    onClick={markAllRead}
                    className="text-xs text-[var(--primary)] hover:underline"
                  >
                    全部标记为已读
                  </button>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifications.map((notification) => (
                    <div 
                      key={notification.id}
                      className={cn(
                        "px-4 py-3 border-b border-[var(--border)] last:border-0 hover:bg-[var(--secondary)] transition-colors cursor-pointer",
                        !notification.read && "bg-[var(--accent)]/30"
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "w-2 h-2 rounded-full mt-1.5 flex-shrink-0",
                          notification.type === 'error' && "bg-[var(--error)]",
                          notification.type === 'warning' && "bg-[var(--warning)]",
                          notification.type === 'info' && "bg-[var(--info)]"
                        )} />
                        <div className="flex-1 min-w-0">
                          <p className={cn(
                            "text-sm",
                            !notification.read ? "font-medium text-[var(--foreground)]" : "text-[var(--muted-foreground)]"
                          )}>
                            {notification.title}
                          </p>
                          <p className="text-xs text-[var(--muted-foreground)] mt-0.5">{notification.time}</p>
                        </div>
                        {!notification.read && (
                          <div className="w-2 h-2 rounded-full bg-[var(--primary)] flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-4 py-2 border-t border-[var(--border)] bg-[var(--secondary)]">
                  <button className="w-full text-center text-sm text-[var(--primary)] hover:underline">
                    查看全部通知
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* 帮助 */}
        <button className="p-2 rounded-lg text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors">
          <HelpCircle className="w-5 h-5" />
        </button>

        {/* 添加设备按钮 */}
        {showAddButton && (
          <button 
            onClick={onAdd}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg font-medium text-sm hover:bg-[var(--primary)]/90 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            添加设备
          </button>
        )}
      </div>
    </header>
  );
}
